import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetProfileQuery } from '../apis/mlkApi/mlk.api.hooks';
import {
  getIsEmailConfirmed,
  setupAddNotesLightSignInOperation,
  setupChooseDeliveryNoneOperation,
  setupChooseDeliveryTimeStrongSignInOperation,
  setupNewAddressLightSignInOperation,
  setupOpeningHoursLightSignInOperation,
  setupPickUpPointStrongSignInOperation,
} from '../helpers/auth';
import { OperationType, RegistrationType } from '../models';
import { getOperationSelector } from '../redux/general/general.selectors';
import { authTokenSelector } from '../redux/user/user.selectors';

export enum StatusOperationEnum {
  SignInOrUp = 'SignInOrUp',
  ConfirmEmail = 'ConfirmEmail',
  CompleteProfile = 'CompleteProfile',
  Nothing = 'Nothing',
}

export const useRegistrationCheck = (operationType: OperationType) => {
  const dispatch = useDispatch();
  const authToken = useSelector(authTokenSelector);
  const operation = useSelector(getOperationSelector);

  const isEmailConfirmed = useMemo(() => {
    if (authToken) {
      return getIsEmailConfirmed(authToken);
    }

    return null;
  }, [authToken]);

  const { data: profile } = useGetProfileQuery(undefined, {
    skip: !authToken || !isEmailConfirmed,
  });

  useEffect(() => {
    if (
      (!authToken || !isEmailConfirmed || !profile?.fiscalCode) &&
      (!operation ||
        operation.operationType === OperationType.FreeReschedule ||
        operation.operationType === OperationType.Reschedule ||
        operation.operationType !== operationType)
    ) {
      switch (operationType) {
        case OperationType.Reschedule:
          setupChooseDeliveryTimeStrongSignInOperation(dispatch);
          break;
        case OperationType.FreeReschedule:
          setupChooseDeliveryNoneOperation(dispatch);
          break;
        case OperationType.Pdr:
          setupPickUpPointStrongSignInOperation(dispatch);
          break;
        case OperationType.AddressChange:
          setupNewAddressLightSignInOperation(dispatch);
          break;
        case OperationType.OpeningHours:
          setupOpeningHoursLightSignInOperation(dispatch);
          break;
        case OperationType.AddNotes:
          setupAddNotesLightSignInOperation(dispatch);
          break;
      }
    }
  }, [
    authToken,
    dispatch,
    isEmailConfirmed,
    operationType,
    profile?.fiscalCode,
  ]);

  const registrationState = useMemo(() => {
    if (operation?.registrationType === RegistrationType.None) {
      return StatusOperationEnum.Nothing;
    } else if (operation?.registrationType === RegistrationType.Light) {
      if (authToken && isEmailConfirmed) {
        return StatusOperationEnum.Nothing;
      } else if (authToken) {
        return StatusOperationEnum.ConfirmEmail;
      } else {
        return StatusOperationEnum.SignInOrUp;
      }
    } else {
      if (profile?.fiscalCode && authToken && isEmailConfirmed) {
        return StatusOperationEnum.Nothing;
      } else if (authToken && !isEmailConfirmed) {
        return StatusOperationEnum.ConfirmEmail;
      } else if (authToken && !profile?.fiscalCode) {
        return StatusOperationEnum.CompleteProfile;
      } else {
        return StatusOperationEnum.SignInOrUp;
      }
    }
  }, [
    authToken,
    isEmailConfirmed,
    operation?.registrationType,
    profile?.fiscalCode,
  ]);

  return { registrationState };
};
